/**
 * Splitting a test history into "recent" and "prior" samples for trend comparison.
 *
 * This exists because both trend calculations in ExamResults previously built windows
 * that OVERLAPPED, which made every delta they produced meaningless:
 *
 *   - the subsection trend used `slice(0, k)` vs `slice(1, k + 1)` AND accumulated both
 *     into the same object, so the delta was provably zero for every possible input;
 *   - the section trend compared tests `[0, n)` against `[1, n]`, so with 20 tests the
 *     two windows shared 19 of them and the "change" was almost entirely noise. With
 *     fewer than 20 tests the prior window was a strict SUBSET of the recent one.
 *
 * Overlap also corrupts the totals, not just the deltas: a test counted in both windows
 * inflates the denominator, which is how the UI ended up rendering "20 / 40 correct"
 * when the student had answered only 30 questions.
 */

/** Never compare more than this many tests on either side. */
export const TREND_WINDOW_MAX = 20;

/** No prior sample exists, so no comparison is possible. Not the same as "no change". */
export const NO_TREND = "N/A" as const;

/**
 * Prior sample scored zero while the current one did not. A relative change is
 * mathematically undefined here (division by zero), so this is a display convention.
 */
export const FROM_ZERO_CHANGE = 100;

export interface TrendWindows<T> {
  recent: T[];
  prior: T[];
}

/**
 * Splits a NEWEST-FIRST list into two disjoint windows.
 *
 * The recent window takes the newer half (capped at `maxWindow`) and the prior window
 * takes the chunk immediately behind it. Disjointness is the entire point — see above.
 *
 * With a single test the prior window is empty, which callers must surface as
 * "not enough history" rather than as a zero change.
 */
export function splitTrendWindows<T>(
  newestFirst: readonly T[],
  maxWindow: number = TREND_WINDOW_MAX
): TrendWindows<T> {
  const recentSize = Math.min(maxWindow, Math.ceil(newestFirst.length / 2));

  return {
    recent: newestFirst.slice(0, recentSize),
    prior: newestFirst.slice(recentSize, recentSize + maxWindow),
  };
}

/**
 * Relative change from `prior` to `current` as a whole-number percentage.
 *
 * Relative, not absolute: going from 30% to 70% correct reports +133%, not +40.
 * That matches what the UI already claimed to show.
 */
export function relativePercentChange(
  current: number,
  prior: number,
  hasPriorSample: boolean
): number | typeof NO_TREND {
  if (!hasPriorSample) return NO_TREND;
  if (!Number.isFinite(current) || !Number.isFinite(prior)) return NO_TREND;
  if (prior === 0) return current === 0 ? 0 : FROM_ZERO_CHANGE;

  return Math.round(((current - prior) * 100) / prior);
}

/** Accumulated correct/total for one window. */
export interface WindowScore {
  correct: number;
  total: number;
}

export function emptyWindowScore(): WindowScore {
  return { correct: 0, total: 0 };
}

/** Percentage correct, 0-100, or 0 when the window is empty. */
export function windowPercent({ correct, total }: WindowScore): number {
  return total > 0 ? Math.round((correct / total) * 100) : 0;
}
